import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Button} from 'antd';
import {CloseOutlined, PlusOutlined} from '@ant-design/icons';
import {
  StyledColumnEditor,
  StyledColumnEditorAddEmpty,
  StyledColumnEditorAddList,
  StyledColumnEditorAddPanel,
  StyledColumnEditorAddRow,
  StyledColumnEditorAddTrigger,
  StyledColumnEditorCount,
  StyledColumnEditorExample,
  StyledColumnEditorHeadRow,
  StyledColumnEditorHeader,
  StyledColumnEditorList,
  StyledColumnEditorLocked,
  StyledColumnEditorRow,
  StyledColumnEditorSavedNotice,
  StyledColumnEditorSearchInput,
} from './StyledColumnEditor';

// The available-fields panel renders inline below the "+ Add field" trigger, toggled by plain local
// state -- not a Dropdown/popupRender overlay (design review, examples/20261027_214611.png: it reads
// as the same Field/Example table as the selected columns above, not a floating checkbox list).
// Selection lives in the caller's state (ExportWizard's `selectedColumns`, never a Form field), so
// this component never meets the Form-unmount class that cost story 7 three reviews.
const ColumnEditor = ({
  eligibleColumns,
  getExampleValue,
  isDefaultSet,
  lockedColumns,
  onAdd,
  onRemove,
  onReset,
  selectedKeys,
}) => {
  const [t] = useTranslation('main');
  const [search, setSearch] = useState('');
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);

  const selectedColumns = useMemo(
    () => eligibleColumns.filter((column) => selectedKeys.includes(column.key)),
    [eligibleColumns, selectedKeys],
  );
  const availableColumns = useMemo(
    () => eligibleColumns.filter((column) => !selectedKeys.includes(column.key)),
    [eligibleColumns, selectedKeys],
  );
  const filteredAvailable = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? availableColumns.filter((column) => column.header_en.toLowerCase().includes(query)) : availableColumns;
  }, [availableColumns, search]);

  const handleAdd = (key) => {
    onAdd(key);
    setSearch('');
  };

  // Toggle, not a one-way reveal -- there is no separate close control in the design, so
  // "+ Add field" doubles as the panel's own collapse button once it's open.
  const handleToggleAddPanel = () => {
    setIsAddPanelOpen((prev) => !prev);
    setSearch('');
  };

  return (
    <StyledColumnEditor>
      <StyledColumnEditorLocked>
        {t('exportWizard.columnEditor.alwaysIncluded', {columns: lockedColumns.map((column) => column.header_en).join(', ')})}
      </StyledColumnEditorLocked>
      <StyledColumnEditorHeader>
        <span>{t('exportWizard.columnEditor.title')}</span>
        {!isDefaultSet && (
          <StyledColumnEditorSavedNotice size='small'>
            <span>{t('exportWizard.columnEditor.savedAsDefault')}</span>
            <Button onClick={onReset} size='small' type='link'>{t('exportWizard.columnEditor.reset')}</Button>
          </StyledColumnEditorSavedNotice>
        )}
        <StyledColumnEditorCount>
          {t('exportWizard.columnEditor.count', {selected: selectedColumns.length, total: eligibleColumns.length})}
        </StyledColumnEditorCount>
      </StyledColumnEditorHeader>
      <StyledColumnEditorList>
        <StyledColumnEditorHeadRow>
          <span>{t('exportWizard.columnEditor.fieldLabel')}</span>
          <span>{t('exportWizard.columnEditor.exampleLabel')}</span>
        </StyledColumnEditorHeadRow>
        {selectedColumns.map((column) => (
          <StyledColumnEditorRow key={column.key}>
            <span>{column.header_en}</span>
            <StyledColumnEditorExample>{getExampleValue(column.key)}</StyledColumnEditorExample>
            <Button
              aria-label={t('exportWizard.columnEditor.removeColumn', {column: column.header_en})}
              icon={<CloseOutlined />}
              onClick={() => onRemove(column.key)}
              size='small'
              type='text'
            />
          </StyledColumnEditorRow>
        ))}
      </StyledColumnEditorList>
      <StyledColumnEditorAddTrigger>
        <Button icon={<PlusOutlined />} onClick={handleToggleAddPanel} size='small' type='link'>
          {t('exportWizard.columnEditor.addField')}
        </Button>
      </StyledColumnEditorAddTrigger>
      {isAddPanelOpen && (
        <StyledColumnEditorAddPanel>
          <StyledColumnEditorSearchInput
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('exportWizard.columnEditor.searchPlaceholder')}
            value={search}
          />
          <StyledColumnEditorAddList>
            {filteredAvailable.length ? filteredAvailable.map((column) => (
              <StyledColumnEditorAddRow key={column.key}>
                <span>{column.header_en}</span>
                <StyledColumnEditorExample>{getExampleValue(column.key)}</StyledColumnEditorExample>
                <Button
                  aria-label={t('exportWizard.columnEditor.addColumn', {column: column.header_en})}
                  icon={<PlusOutlined />}
                  onClick={() => handleAdd(column.key)}
                  size='small'
                  type='text'
                />
              </StyledColumnEditorAddRow>
            )) : (
              <StyledColumnEditorAddEmpty>{t('exportWizard.columnEditor.noMatches')}</StyledColumnEditorAddEmpty>
            )}
          </StyledColumnEditorAddList>
        </StyledColumnEditorAddPanel>
      )}
    </StyledColumnEditor>
  );
};

ColumnEditor.propTypes = {
  eligibleColumns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    header_en: PropTypes.string.isRequired,
  })).isRequired,
  getExampleValue: PropTypes.func.isRequired,
  isDefaultSet: PropTypes.bool.isRequired,
  lockedColumns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    header_en: PropTypes.string.isRequired,
  })).isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  selectedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ColumnEditor;
